<template>
  <div
    id="vueproduct"
    :key="upid"
    :class="
      bemBlockMod({
        modal: context === CONTEXT_MODAL,
        'no-size-choice': !hasSizeChoice,
        'no-variant-choice': !hasSizeChoice && !hasColorChoice,
      })
    "
    :data-get-content-asset="getContentAssetUrl"
  >
    <BaseBackButton
      v-if="context === CONTEXT_ROUTER_VIEW && !shouldHideExternalBackButton"
    />
    <div
      class="product-detail product-wrapper product-detail-vue"
      :data-context="context"
      :data-pid="product.id"
    >
      <BaseProductDataForSfra :product="product" />
      <meta itemprop="sku" :content="product.id" />
      <meta itemprop="mpn" :content="product.id" />
      <meta
        v-if="product.longDescription"
        itemprop="description"
        :content="product.longDescription"
      />
      <meta itemprop="brand" content="BIMBA Y LOLA" />
      <div
        ref="imagesContainer"
        :class="
          bemElement(
            isBreakpointDesktop ? 'images-container' : 'carousel-container'
          )
        "
      >
        <BaseTag
          v-if="!isBreakpointDesktop && showVirtualTryOnTag"
          type="detail"
          subtype="secondary"
          class="iframe-tag"
          :text="$msg('tag.iframe', 'product')"
          :is-custom="true"
          @click.native="openIframeModal"
        />
        <BaseProductActionableLink
          v-if="!isBreakpointDesktop"
          v-show="product.bylActionableLink"
          bem="the-product-details-actionable-link"
          :class="bemElement('actionable-link')"
          :product="product"
          @actionable-link:clicked="baseActionableLinkDisplayOpen = true"
        />
        <div v-if="!hasImages" :class="bemElement('pre-placeholder')">
          <span :class="[bemElement('pic'), 'img']" />
        </div>
        <BaseImage
          v-for="(imageSet, imageIndex) in productImageSets"
          :key="imageIndex"
          :class="bemElement('pic')"
          :src="getDefaultImageUrlFromProductImageSet(imageSet)"
          :alt="getDefaultImageAltFromProductImageSet(imageSet)"
          :loading="imageIndex < 1 || areCriticalImagesLoaded ? 'eager' : null"
          :placeholder="getPlaceholderImageUrlFromProductImageSet(imageSet)"
          :sizes="imageAttributeSizes.PDP_GALLERY"
          :srcset="getSrcsetFromProductImageSet(imageSet)"
          @image-click="(event) => handleProductImageClick(event, imageIndex)"
          @load="handleImageLoad(imageIndex)"
        />
        <BaseCarousel
          v-if="$windowWidth < 575 && hasImages"
          :class="bemElement('carousel')"
          :items="productImageSets"
          @carousel-click="
            (event, currentIndex) =>
              handleProductImageClick(event, currentIndex)
          "
        >
          <template #default="slotProps">
            <BaseImage
              :ref="`carouselItem${slotProps.index}`"
              :class="slotProps.classname"
              :src="getDefaultImageUrlFromProductImageSet(slotProps.item)"
              :alt="getDefaultImageAltFromProductImageSet(slotProps.item)"
              :image-classes="bemElement('image')"
              :loading="
                slotProps.index === 0 || areCriticalImagesLoaded
                  ? 'eager'
                  : null
              "
              :placeholder="
                getPlaceholderImageUrlFromProductImageSet(slotProps.item)
              "
              :sizes="imageAttributeSizes.PDP_GALLERY"
              :srcset="getSrcsetFromProductImageSet(slotProps.item)"
              @load="handleImageLoad(slotProps.index)"
            />
          </template>
        </BaseCarousel>
      </div>
      <div :class="bemElement('text-container')">
        <div ref="infoContainer" :class="bemElement('sticky-container')">
          <div
            class="interaction--loading-content"
            :class="{ 'interaction-active': !loading }"
            data-interaction="interaction--loading-content"
          >
            <div class="loading-content">
              <BaseSpinner small grey />
            </div>
            <div class="loaded-content">
              <div :class="bemElement('sticky-child-container')">
                <p v-if="isCustomTag" :class="bemElement('tag', 'custom')">
                  <BaseTag
                    type="detail"
                    subtype="primary"
                    :text="customTag.name"
                    :text-color="customTag.color"
                    :is-custom="isCustomTag"
                  />
                </p>
                <div :class="bemElement('title-wrapper')">
                  <h1 :class="bemElement('title')" itemprop="name">
                    {{ product.productName }}
                  </h1>
                  <BaseWishlistHeart
                    v-if="
                      isWishlistEnabled &&
                      product.assignedToSiteCatalog &&
                      !isCustomizable
                    "
                    :class="bemElement('add-to-wishlist')"
                    :product-id="product.id"
                    :product="product"
                    :is-pdp-context="true"
                  />
                </div>
                <BasePrice
                  v-if="product.assignedToSiteCatalog"
                  bem="the-product-details-price"
                  :product="product"
                  :show-previous-price-msg="true"
                  :show-previous-price-msg-tooltip="true"
                />
              </div>
              <BaseProductColorSwatches
                v-if="product.assignedToSiteCatalog"
                bem="the-product-details-swatches"
                :class="bemElement('swatches')"
                :product="product"
                :show-current="true"
                :product-detail="true"
                :plain-images="true"
              />
              <p
                v-if="!isMarketWithoutSale && onlineExclusive"
                :class="bemElement('online-exclusive')"
              >
                <BaseTag
                  type="detail"
                  subtype="secondary"
                  :text="$msg('tag.exclusive', 'product')"
                />
              </p>
              <!-- Empty anchor for the Fit Analytics «find your size» button,
              which its own pdp.js injects. Same class on both layouts, so the
              anchor no longer depends on how Fit Analytics tells mobile apart. -->
              <div
                v-if="!isBreakpointDesktop && product.assignedToSiteCatalog"
                :class="bemElement('fit-analytics')"
              />
              <BaseTag
                v-if="isBreakpointDesktop && showVirtualTryOnTag"
                class="iframe-tag"
                type="detail"
                subtype="secondary"
                :text="$msg('tag.iframe', 'product')"
                :is-custom="true"
                @click.native="openIframeModal"
              />
              <BaseProductActionableLink
                v-if="isBreakpointDesktop"
                v-show="product.bylActionableLink"
                bem="the-product-details-actionable-link"
                :class="bemElement('actionable-link')"
                :product="product"
                @actionable-link:clicked="baseActionableLinkDisplayOpen = true"
              />
              <TheProductDetailsSizeSelector
                v-if="isBreakpointDesktop && product.assignedToSiteCatalog"
                ref="desktopSizeSelector"
                :class="bemElement('sizes', { 'no-stock': !product.available })"
                :product="product"
                :show-size-conversion="true"
                @size:change="handleSizeChange"
              />
              <div
                v-if="isBreakpointDesktop && product.assignedToSiteCatalog"
                :class="bemElement('fit-analytics')"
              />
              <BaseModalCTA
                v-if="
                  !isMarketWithoutSale &&
                  isStockAlertEnabled &&
                  isStockAlertAvailable &&
                  product.assignedToSiteCatalog
                "
                :class="[bemElement('actions')]"
                :show-modal-close="true"
                :hide-button-on-open="true"
                :button-text="$msg('button.stockalert', 'product')"
                :is-open="isStockAlertModalOpen"
                :no-padding="true"
                @modal:change-state="handleStockAlertModalState"
                @modal:close-all="handleModalClose"
              >
                <TheProductDetailsStockAlert
                  :product="product"
                  :is-disabled="isBreakpointDesktop && !selectedSize"
                  :stock-alert-visible="isStockAlertModalOpen"
                  :stock-alert-size="selectedSize || {}"
                  @stock-alert:hide="handleStockAlertModalState"
                />
              </BaseModalCTA>
              <p
                v-if="
                  !isStockAlertEnabled &&
                  !hasSomeSelectableSize() &&
                  product.assignedToSiteCatalog
                "
                :class="bemElement('sold-out')"
              >
                {{ $msg('tag.coming_unavailable', 'product') }}
              </p>
              <div
                v-if="
                  isCustomizable &&
                  !isStockAlertAvailable &&
                  !isAppExclusive &&
                  product.assignedToSiteCatalog &&
                  !isMarketWithoutSale
                "
                :class="[
                  bemElement('customize', ['desktop']),
                  bemElement('actions'),
                ]"
              >
                <button
                  :class="[
                    bemElement('customize-btn', ['desktop']),
                    'btn',
                    'btn-primary',
                    'btn-big',
                  ]"
                  :data-pid="product.id"
                  data-toggle="modal"
                  data-target=""
                  @click="willOpenCustomizeModal = !willOpenCustomizeModal"
                >
                  <span class="default-content">
                    {{ $msg('button.customize', 'product') }}
                  </span>
                </button>
              </div>

              <div
                v-if="isButtonDesktopAvailable"
                :class="[
                  bemElement('add-to-cart'),
                  bemElement('actions'),
                  'cart-and-ipay',
                ]"
              >
                <input
                  type="hidden"
                  class="add-to-cart-url"
                  :value="addToCartUrl"
                />
                <button
                  :class="[
                    bemElement('add-to-cart-btn', ['desktop']),
                    'add-to-cart',
                    'btn',
                    'btn-primary',
                    'btn-big',
                    'interaction--add-to-bag',
                  ]"
                  :data-pid="product.id"
                  data-toggle="modal"
                  data-target="#chooseBonusProductModal"
                  data-interaction="interaction--add-to-bag"
                  :data-interaction-state="initialStateAddToBag"
                >
                  <BaseSpinner small />
                  <span class="default-content">
                    {{ $msg('button.addtocart', 'product') }}
                  </span>
                  <span class="swap-content">
                    {{ $msg('text.alert.addedtobasket', 'product') }}
                  </span>
                </button>

                <p :class="bemElement('add-to-cart-error')">
                  {{ $msg('error.size.unselected', 'product') }}
                </p>
              </div>
              <a
                v-if="isMarketWithoutSale && product.assignedToSiteCatalog"
                :class="[
                  bemElement('find-stores'),
                  bemElement('actions'),
                  'btn',
                  {
                    'btn--cta': isBreakpointMobile,
                    'btn-primary btn-big': !isBreakpointMobile,
                  },
                ]"
                :href="findStoresUrl"
              >
                {{ $msg('button.find.store', 'product') }}
              </a>
              <p
                v-if="
                  isMarketWithoutSale &&
                  availabilityMessage.length > 0 &&
                  product.assignedToSiteCatalog
                "
                :class="bemElement('informative-claim')"
                v-html="availabilityMessage"
              />
              <div
                v-if="isAppExclusive && product.assignedToSiteCatalog"
                :class="[
                  bemElement('app-exclusive'),
                  bemElement('actions'),
                  'app-exclusive-block',
                ]"
              >
                <p class="app-exclusive-block__description-text">
                  {{ $msg('appexclusive.description', 'product') }}
                </p>
                <a class="app-exclusive-block__link" :href="appsLandingUrl">
                  {{ $msg('appexclusive.link', 'product') }}
                </a>
              </div>
              <BaseModalCTA
                v-if="
                  productAddedToCart &&
                  isBreakpointMobile &&
                  product.assignedToSiteCatalog
                "
                :class="bemElement('actions')"
                :is-open="true"
                :no-padding="true"
              >
                <p :class="[bemElement('text')]">
                  {{ addedMessage }}
                </p>
                <a
                  :href="cartShowUrl"
                  :class="[
                    bemElement('btn-modal', 'btn-link'),
                    'btn',
                    'btn--cta',
                  ]"
                >
                  {{ $msg('button.productcard', 'product') }}
                </a>
              </BaseModalCTA>
              <button
                v-if="
                  isButtonMobileAvailable &&
                  (isStockAlertEnabled || hasSomeSelectableSize()) &&
                  product.assignedToSiteCatalog
                "
                :class="[
                  bemElement('add-to-cart-btn', 'mobile'),
                  bemElement('actions'),
                  'add-to-cart-mobile',
                  'btn',
                  'btn--cta',
                  'interaction--add-to-bag-mobile',
                ]"
                :data-pid="product.id"
                :data-size-type="mobileTypeBySizeButton"
                :data-attr-url="hasUniqueSelectableSize() ? sizes[0].url : null"
                data-toggle="modal"
                data-target="#chooseBonusProductModal"
                data-interaction="interaction--add-to-bag-mobile"
                :data-interaction-state="initialStateAddToBagMobile"
              >
                <BaseSpinner small white />
                <span class="default-content">
                  <span v-if="hasUniqueSelectableSize()" class="one-size">
                    {{
                      hasNoStock
                        ? $msg('button.stockalert', 'product')
                        : $msg('button.addtocart', 'product')
                    }}
                  </span>

                  <BaseModalCTA
                    v-else
                    class="multiple-sizes"
                    :show-modal-close="true"
                    :button-text="$msg('button.sizeselect', 'product')"
                    :is-open="isSizeSelectorMobileShown"
                    @modal:change-state="handleSizeSelectorMobileState"
                    @modal:close-all="handleModalClose"
                  >
                    <TheProductDetailsSizeSelector
                      ref="mobileSizeSelector"
                      :product="product"
                      :show-size-conversion="true"
                      @size:change="handleSizeChange"
                    />
                  </BaseModalCTA>
                </span>

                <span class="swap-content">
                  {{ addedMessage }}
                </span>
              </button>
              <div
                v-if="
                  isCustomizable &&
                  !isStockAlertAvailable &&
                  !isAppExclusive &&
                  product.assignedToSiteCatalog &&
                  !isMarketWithoutSale
                "
                :class="[
                  bemElement('customize', ['mobile']),
                  bemElement('actions'),
                ]"
              >
                <button
                  :class="[
                    bemElement('actions'),
                    'btn',
                    'btn--cta',
                    'btn-big',
                    'customize-btn--mobile',
                  ]"
                  :data-pid="product.id"
                  data-toggle="modal"
                  data-target=""
                  @click="willOpenCustomizeModal = !willOpenCustomizeModal"
                >
                  <span class="default-content">
                    {{ $msg('button.customize', 'product') }}
                  </span>
                </button>
              </div>
              <button
                v-if="
                  !isMarketWithoutSale &&
                  !isStockAlertAvailable &&
                  isBreakpointMobile &&
                  isAppExclusive &&
                  product.assignedToSiteCatalog
                "
                :class="[
                  bemElement('app-exclusive-btn', 'mobile'),
                  bemElement('actions'),
                  'btn',
                  'btn--cta',
                ]"
              >
                <BaseModalCTA
                  class="app-exclusive-block"
                  :is-open="false"
                  :show-modal-close="true"
                  :button-text="
                    isAppExclusiveModalOpen
                      ? $msg('appexclusive.link', 'product')
                      : $msg('button.appexclusive', 'product')
                  "
                  @modal:change-state="handleAppExclusiveModalState"
                >
                  <p class="app-exclusive-block__description-text">
                    {{ $msg('appexclusive.description', 'product') }}
                  </p>
                </BaseModalCTA>
              </button>
              <BaseSlotMessage
                slot-id="pdp-message"
                :class="bemElement('message')"
              />
            </div>
          </div>
          <div
            v-if="product.assignedToSiteCatalog"
            :class="[
              bemElement('accordion-container'),
              'expand-elements-wrapper',
            ]"
          >
            <button
              :class="[bemElement('size-guide-btn'), 'btn', 'btn--primary']"
              data-interaction="modal"
              data-modal-type="size-guide"
              data-modal-close="true"
              :data-modal-content-url="helpGetSizeGuideUrl"
              @click="handleSizeGuideButtonClick"
            >
              {{ $msg('link.size_guide', 'product') }}
            </button>
            <BaseAccordion
              :title="$msg('details.title', 'product')"
              :group-name="groupNameToToggle"
            >
              <ul>
                <li>
                  {{ 'Ref: ' + pidWithoutSize }}
                </li>
                <li
                  v-if="!!product.longDescription"
                  :class="bemElement('details-description')"
                  v-html="product.longDescription"
                />
                <li>
                  <TheProductDetailsAdditionalInfo
                    :class="bemElement('details-additional-info')"
                    :product="product"
                    :show-online-exclusive="false"
                  />
                </li>
                <li
                  v-if="!!product.bylDetailsAdditional"
                  :class="bemElement('details-additional')"
                  v-html="product.bylDetailsAdditional"
                />
                <li
                  v-if="!!product.bylCompositionText"
                  :class="bemElement('composition')"
                  v-html="product.bylCompositionText"
                />
                <li
                  v-if="!!product.bylWashing"
                  :class="bemElement('details-list')"
                >
                  <span
                    v-for="(washingText, washingIndex) in product.bylWashing"
                    :key="washingIndex"
                    :class="bemElement('details-item')"
                    >{{ washingText }}</span
                  >
                </li>
                <li
                  v-if="product.bylMadeIn"
                  :class="[bemElement('made-in'), bemElement('text-line')]"
                >
                  {{ $msg('label.madein', 'product') + ' ' }}
                  <span :class="bemElement('made-in-country')">
                    {{ product.bylMadeIn }}
                  </span>
                </li>
              </ul>
            </BaseAccordion>

            <BaseAccordion
              v-if="!isMarketWithoutSale"
              :title="$msg('deliveryreturns.title', 'product')"
              :group-name="groupNameToToggle"
              accordion-id="deliveryAndReturns"
              :class="[
                'delivery-and-returns',
                bemElement('delivery-and-returns'),
              ]"
            >
              <div v-html="$msg('msg.pdp.delivery.returns.info', 'product')" />
            </BaseAccordion>

            <BaseAccordion
              v-if="!isMarketWithoutSale"
              :title="$msg('paymentMethods.title', 'product')"
              :group-name="groupNameToToggle"
              accordion-id="paymentMethods"
              :class="['payment-methods', bemElement('payment-methods')]"
            >
              <div v-html="$msg('msg.pdp.payment.methods.info', 'product')" />
            </BaseAccordion>

            <button
              v-if="isInStoreStockEnabled"
              :class="[bemElement('store-stock-btn'), 'btn', 'btn--primary']"
              @click="willOpenInStoreStockModal = !willOpenInStoreStockModal"
            >
              {{ $msg('instorestock.title', 'inStoreStock') }}
            </button>

            <button
              v-if="isChatButtonPDPEnabled"
              ref="chatButton"
              :class="[
                bemElement('chat-button'),
                'chat-button',
                'btn',
                'btn--primary',
              ]"
              @click="handleChatButtonClick"
            >
              {{ $msg('button.chat', 'product') }}
            </button>

            <BaseShareProductButton
              v-if="webShareApiSupported && isBreakpointMobile"
              :product-name="product.productName"
              :class="[bemElement('share-btn'), 'btn', 'btn--primary']"
            />
          </div>
        </div>
      </div>
    </div>
    <BaseProductSlider
      v-if="enoughCompleteThelookProducts && product.assignedToSiteCatalog"
      :class="bemElement('complete-the-look')"
      :heading="$msg('completethelook.title', 'product')"
      :products="product.bylCompleteTheLook.slice(0, 10)"
      :tile-context="pdpSlider"
    />
    <BaseProductSlider
      v-if="recommendedProducts.length > 0 && product.assignedToSiteCatalog"
      :class="bemElement('recommendations')"
      :heading="recommendationsHeading"
      :products="recommendedProducts"
      :wrap-items="true"
      :tile-context="pdpRecommendedSlider"
      :hide-product-names="recommendationsDisplay.hideProductNames"
      :hide-prices="recommendationsDisplay.hidePrices"
      :hide-discount="recommendationsDisplay.hideDiscount"
      :hide-tags="recommendationsDisplay.hideTags"
      :is-recommendation-from-einstein="isRecommendationFromEinstein"
      :recommender-name="recommendationsRecommenderName"
    />
    <TheProductDetailsZoom
      :class="{
        'is-visible': isZoomOpen,
      }"
      :is-visible="isZoomOpen"
      :images="productImageSets"
      :loading="areGalleryImagesLoaded ? 'eager' : null"
      :zoom-requirements="zoomRequirements"
      @close-click="closeZoom(true)"
    />
    <TheInStoreStockModal
      v-if="willOpenInStoreStockModal"
      :product="product"
      @in-store-stock-modal-closed="willOpenInStoreStockModal = false"
    />
    <BaseModal
      v-if="iframeUrl"
      :is-open="showIframeModal"
      :modal-modifier="'ar-try-on'"
      :show-backdrop="true"
      :close-on-backdrop-interaction="true"
      @closed="showIframeModal = false"
    >
      <iframe
        allow="camera; web-share"
        width="100%"
        height="100%"
        frameBorder="0"
        :src="iframeUrl"
      />
    </BaseModal>
    <TheProductDetailsCustomizationModal
      v-if="willOpenCustomizeModal"
      :product="product"
      :add-to-cart-url="addToCartUrl"
      :add-to-cart-url-mobile="sizes[0].url"
      @customize-modal-closed="willOpenCustomizeModal = false"
    />
    <BaseActionableLinkDisplay
      v-if="product.bylActionableLink"
      :is-open="baseActionableLinkDisplayOpen"
      :product="product"
      modal-modifier="pdp"
      @closed="baseActionableLinkDisplayOpen = false"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EventBusConfig from '../config/eventBusConfig';
import bemClassesMixin from '../mixins/bemClassesMixin';
import { customizableProductMixin } from '../mixins/customizableProductMixin';
import { productImageSetMixin } from '../mixins/productImageSetMixin';
import { productBaseMixin } from '../mixins/productPropBaseMixin';
import { productColorMixin } from '../mixins/productPropColorMixin';
import { productPriceMixin } from '../mixins/productPropPriceMixin';
import { productSizesMixin } from '../mixins/productPropSizesMixin';
import productRecommendationsMixin from '../mixins/productRecommendationsMixin';
import productTagsMixin from '../mixins/productTagsMixin';
import sfraProductMixin from '../mixins/sfraProductMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import BaseActionableLinkDisplay from './actionableLink/BaseActionableLinkDisplay.vue';
import BaseAccordion from './BaseAccordion.vue';
import BaseBackButton from './BaseBackButton.vue';
import BaseCarousel from './BaseCarousel.vue';
import BaseProductColorSwatches from './BaseProductColorSwatches.vue';
import BaseProductActionableLink from './actionableLink/BaseProductActionableLink.vue';
import BaseImage from './BaseImage.vue';
import BaseModal from './BaseModal.vue';
import BaseModalCTA from './BaseModalCTA.vue';
import BasePrice from './BasePrice.vue';
import BaseProductDataForSfra from './BaseProductDataForSfra.vue';
import BaseProductSlider from './BaseProductSlider.vue';
import BaseSpinner from './BaseSpinner.vue';
import BaseTag from './BaseTag.vue';
import TheInStoreStockModal from './TheInStoreStockModal.vue';
import TheProductDetailsAdditionalInfo from './TheProductDetailsAdditionalInfo.vue';
import TheProductDetailsCustomizationModal from './TheProductDetailsCustomizationModal.vue';
import BaseSlotMessage from './BaseSlotMessage.vue';
import TheProductDetailsSizeSelector from './TheProductDetailsSizeSelector.vue';
import TheProductDetailsStockAlert from './TheProductDetailsStockAlert.vue';
import BaseWishlistHeart from './BaseWishlistHeart.vue';
import TheProductDetailsZoom from './TheProductDetailsZoom.vue';
import { getters, types } from '../store';
import { STATE_ADD_TO_BAG } from '../../interactions/add-to-bag';
import { STATE_ADD_TO_BAG_MOBILE } from '../../interactions/add-to-bag-mobile';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import getFrontConfig from '../../common/getFrontConfig';
import BaseShareProductButton from './BaseShareProductButton.vue';
import { getVariationGroupId } from '../../common/helpers/productIdHelpers';
import pageDataMixin from '../mixins/pageDataMixin';
import pdpDualScrollMixin from '../mixins/pdpDualScrollMixin';
import { PAGE_TYPES } from '../../../../../config/analytics';

const analyticsEventBus = getAnalyticsEventBus();
const FRONT_CONFIG = getFrontConfig();
const GROUP_NAME_TO_TOGGLE = 'product-detail-page';

/*
Resolutions - PDP img size

1 foto (< 1024)
375 x ...: 375x563 (1x), 750x1126 (2x)
768 x ...: 512x768 (1x), 1024x1536 (2x)

2 fotos contiguas (>= 1024)
1024 x ...: 385x578 (1x), 770x1156 (2x)
1440 x 900: 543x815 (1x), 1086x1630 (2x)
2560 x 1080: 944x1415

Zoom 100vw: 375, 768, 1024, 1440, 2560, 5120...

srcset:
480px,
640px,
768px,
1080px,
1280px,
1440px,
1920px
*/
const SIZES = {
  PDP_GALLERY: '(min-width: 1024px) 37.5vw, (min-width: 768px) 66.6vw, 100vw',
  PDP_ZOOM: '100vw',
};

export default {
  name: 'TheProductDetails',

  components: {
    BaseAccordion,
    BaseActionableLinkDisplay,
    BaseBackButton,
    BaseCarousel,
    BaseImage,
    BaseModal,
    BaseModalCTA,
    BasePrice,
    BaseProductActionableLink,
    BaseProductColorSwatches,
    BaseProductDataForSfra,
    BaseProductSlider,
    BaseShareProductButton,
    BaseSlotMessage,
    BaseSpinner,
    BaseTag,
    BaseWishlistHeart,
    TheInStoreStockModal,
    TheProductDetailsAdditionalInfo,
    TheProductDetailsCustomizationModal,
    TheProductDetailsSizeSelector,
    TheProductDetailsStockAlert,
    TheProductDetailsZoom,
  },

  mixins: [
    bemClassesMixin,
    customizableProductMixin,
    productBaseMixin,
    productColorMixin,
    productImageSetMixin,
    productPriceMixin,
    productSizesMixin,
    productRecommendationsMixin,
    productTagsMixin,
    sfraProductMixin,
    windowResizeMixin,
    pageDataMixin,
    pdpDualScrollMixin,
  ],

  props: {
    addToCartUrl: {
      type: String,
      required: true,
    },
    context: {
      type: String,
      validator: (prop) =>
        [
          FRONT_CONFIG.vue.TheProductDetails.context.modal,
          FRONT_CONFIG.vue.TheProductDetails.context.routerView,
        ].includes(prop),
      default: FRONT_CONFIG.vue.TheProductDetails.context.routerView,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      appsLandingUrl: `${FRONT_CONFIG.urls.pageShow}?cid=apps`,
      availabilityMessage: '',
      cartShowUrl: FRONT_CONFIG.urls.cartShow,
      CONTEXT_MODAL: FRONT_CONFIG.vue.TheProductDetails.context.modal,
      CONTEXT_ROUTER_VIEW:
        FRONT_CONFIG.vue.TheProductDetails.context.routerView,
      completeTheLookMinimum: 1,
      dataChangeBreakpoint: FRONT_CONFIG.responsive.bp_575,
      findStoresUrl: FRONT_CONFIG.urls.storeLocator,
      getContentAssetUrl: FRONT_CONFIG.urls.getContentAssetUrl,
      groupNameToToggle: GROUP_NAME_TO_TOGGLE,
      helpGetSizeGuideUrl: FRONT_CONFIG.urls.helpGetSizeGuideUrl,
      imageAttributeSizes: SIZES,
      initialStateAddToBagMobile: STATE_ADD_TO_BAG_MOBILE.ENABLED,
      isChatButtonPDPEnabled: FRONT_CONFIG.chatConfiguration?.pdpEnabled,
      willOpenInStoreStockModal: false,
      willOpenCustomizeModal: false,
      isInStoreStockEnabled: FRONT_CONFIG.inStoreStockEnabled,
      isAppExclusiveModalOpen: false,
      isMarketWithoutSale: FRONT_CONFIG.isMarketWithoutSale,
      isSizeSelectorMobileShown: false,
      isStockAlertModalOpen: false,
      isWishlistEnabled: FRONT_CONFIG.isWishlistEnabled,
      isZoomOpen: false,
      loadedImagesIndexes: [],
      productAddedToCart: null,
      selectedSize: null,
      zoomRequirements: {},
      pdpSlider: FRONT_CONFIG.vue.BaseProductTile.context.pdpSlider,
      pdpRecommendedSlider:
        FRONT_CONFIG.vue.BaseProductTile.context.pdpRecommendedSlider,
      showIframeModal: false,
      baseActionableLinkDisplayOpen: false,
    };
  },

  computed: {
    ...mapGetters({
      shouldHideExternalBackButton: getters.header.shouldHideExternalBackButton,
    }),
    /** Critical images are those which should be loaded first.
     * - Mobile: 1st image
     * - Desktop: 1st and 2nd images
     * @returns {boolean} - true or false
     */
    areCriticalImagesLoaded() {
      if (this.isBreakpointMobile) {
        return this.loadedImagesIndexes.indexOf(0) > -1;
      }
      return (
        this.loadedImagesIndexes.indexOf(0) > -1 &&
        this.loadedImagesIndexes.indexOf(1) > -1
      );
    },
    areGalleryImagesLoaded() {
      return (
        this.loadedImagesIndexes.indexOf(this.productImageSets.length - 1) > -1
      );
    },
    enoughCompleteThelookProducts() {
      return (
        this.product?.bylCompleteTheLook &&
        this.product?.bylCompleteTheLook.length >= this.completeTheLookMinimum
      );
    },
    initialStateAddToBag() {
      return this.hasUniqueSelectableSize()
        ? STATE_ADD_TO_BAG.ENABLED
        : STATE_ADD_TO_BAG.DISABLED;
    },
    isAppExclusive() {
      return (
        FRONT_CONFIG.isAppExclusiveEnabled &&
        (this.product?.bylAppExclusive ?? false)
      );
    },
    hasImages() {
      return this.productImages['pdp-large']?.length >= 1;
    },
    /**
     * Whether there are colors to pick: with one, the selector paints nothing
     * (same check as BaseProductColorSwatches)
     * @returns {boolean} true with more than one color
     */
    hasColorChoice() {
      return this.colors.length > 1;
    },
    /**
     * Whether there are sizes to pick: with one, the selector is hidden
     * (same check as TheProductDetailsSizeSelector)
     * @returns {boolean} true with more than one size
     */
    hasSizeChoice() {
      return this.sizes.length > 1;
    },
    showSizeMessage() {
      const someStock = this.sizes.some((elem) => elem.selectable);
      const someNoStock = this.sizes.some((elem) => !elem.selectable);
      return someStock && someNoStock;
    },
    hasNoStock() {
      return (
        !this.isMarketWithoutSale &&
        !this.product?.bylComingSoon &&
        !this.product?.available
      );
    },
    isButtonDesktopAvailable() {
      return (
        !this.isMarketWithoutSale &&
        !this.isStockAlertAvailable &&
        !this.isAppExclusive &&
        this.product?.assignedToSiteCatalog &&
        !this.isCustomizable
      );
    },
    isButtonMobileAvailable() {
      return (
        !this.isMarketWithoutSale &&
        !this.isStockAlertAvailable &&
        this.isBreakpointMobile &&
        !this.productAddedToCart &&
        !this.isAppExclusive &&
        !this.isCustomizable
      );
    },
    isSelectedSizeUnavailable() {
      return this.selectedSize && !this.selectedSize.selectable;
    },
    isStockAlertEnabled() {
      return this.product?.bylStockAlertEnabled;
    },
    isStockAlertAvailable() {
      return (
        this.isSelectedSizeUnavailable ||
        (!this.hasSomeSelectableSize() && this.isBreakpointDesktop)
      );
    },
    mobileTypeBySizeButton() {
      return this.hasUniqueSelectableSize() ? 'one-size' : 'multiple-size';
    },
    addedMessage() {
      const lastSelectedSize = this.getLastSelectedSize()?.displayValue;
      let msg = '';

      if (lastSelectedSize === 'UN' || !lastSelectedSize) {
        msg = this.$msg('text.alert.addedtobasket.modal', 'product');
      } else {
        msg = this.$msg('button.sizeaddedmobile', 'product', [
          lastSelectedSize,
        ]);
      }

      return msg;
    },
    pidWithoutSize() {
      return getVariationGroupId(this.product?.id || '');
    },
    productImageSets() {
      return this.getProductImageSets(FRONT_CONFIG.productImages.imageSets.PDP);
    },
    isVariationGroup() {
      return this.product?.productType === 'variationGroup';
    },
    showVirtualTryOnTag() {
      return this.iframeUrl && !this.product?.bylActionableLink;
    },
    webShareApiSupported() {
      return navigator.share;
    },
  },

  watch: {
    loading() {
      this.pageTitle = this.product?.productName || '';
      this.emitAnalyticsData();
    },
    pidWithoutSize() {
      this.selectedSize = null;
    },
    isStockAlertModalOpen() {
      if (this.isStockAlertModalOpen) {
        this.closeAllAccordions();
      }
    },
  },

  async created() {
    if (this.isMarketWithoutSale) {
      this.availabilityMessage = this.$msg(
        'msg.pdp.availability.message',
        'product'
      );
    }
  },

  mounted() {
    if (window?.gaInfo?.pageType === PAGE_TYPES.product_look) {
      this.pageType = PAGE_TYPES.product_look_pdp;
    } else {
      this.pageType = PAGE_TYPES.product;
    }
    this.$nextTick(() => {
      this.emitAnalyticsData();
    });
    this.$eventBus?.$on(
      'base-accordion:toggle-expand',
      this.handleAccordionEvent
    );
    this.$eventBus?.$on(
      EventBusConfig.events.TheProductDetails.addedToCart,
      this.handleProductAddedToCart
    );
    this.$eventBus?.$on(
      EventBusConfig.events.TheProductDetails.colorSelected,
      this.handleColorChange
    );
  },

  beforeDestroy() {
    this.$eventBus?.$off(
      'base-accordion:toggle-expand',
      this.handleAccordionEvent
    );
    this.$eventBus?.$off(
      EventBusConfig.events.TheProductDetails.addedToCart,
      this.handleProductAddedToCart
    );
    this.$eventBus?.$off(
      EventBusConfig.events.TheProductDetails.colorSelected,
      this.handleColorChange
    );
    window.removeEventListener('popstate', this.closeZoom);
  },

  methods: {
    closeAllAccordions() {
      if (this.isBreakpointDesktop) {
        this.$eventBus.$emit('base-accordion:toggle-expand', {
          groupNameToToggle: this.groupNameToToggle,
          accordionExpanded: null,
        });
      }
    },
    closeZoom() {
      if (!this.isZoomOpen) return;
      this.isZoomOpen = false;
      this.$emit('zoomclose');
      this.$eventBus.$emit(EventBusConfig.events.TheProductDetails.zoomClosed);
    },
    handleChatButtonClick() {
      const { chatButton } = this.$refs;
      if (chatButton) {
        document.dispatchEvent(
          new CustomEvent('init-bootstrap-chat', { detail: 'Producto' })
        );
      }
      // Send info for Analytics
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.productDetailsItemClicked,
        analyticsEventBus.EVENTS.PAYLOADS.productDetailsItemClicked.item.chat
      );
    },
    handleSizeChange(size) {
      this.selectedSize = size;
      this.isStockAlertModalOpen = this.isSelectedSizeUnavailable;
      if (this.isBreakpointDesktop) {
        this.sfraSelectSize(size);
      }
    },
    handleSizeGuideButtonClick() {
      // Send info for Analytics
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.productDetailsItemClicked,
        analyticsEventBus.EVENTS.PAYLOADS.productDetailsItemClicked.item
          .sizeGuide
      );
    },
    handleSizeSelectorMobileState({ isModalOpen = false } = {}) {
      this.isSizeSelectorMobileShown = isModalOpen;
    },
    getZoomPercentileY(event, index) {
      const clickedItem =
        this.$refs[`carouselItem${index}`]?.$el || event.currentTarget;
      const clickedElementTop = clickedItem?.getBoundingClientRect()
        ? clickedItem.getBoundingClientRect().top + window.scrollY
        : 0;
      const relativeTopToImage =
        event.pageY >= clickedElementTop ? event.pageY - clickedElementTop : 0;
      // eslint-disable-next-line no-unsafe-optional-chaining
      return relativeTopToImage / clickedItem?.offsetHeight;
    },
    handleAccordionEvent({ groupNameToToggle, accordionExpanded }) {
      const shouldCloseStockAlertModal =
        accordionExpanded &&
        groupNameToToggle === this.groupNameToToggle &&
        this.isStockAlertModalOpen;
      if (shouldCloseStockAlertModal) {
        this.handleStockAlertModalState({ uncheckSizeInputs: true });
      }
    },
    handleColorChange() {
      this.$store.commit(types.product.loading, true);
      this.uncheckSizeInputs();
    },
    handleImageLoad(index) {
      this.loadedImagesIndexes.push(+index);
    },
    handleProductImageClick(event, index = null) {
      this.openZoom(index, this.getZoomPercentileY(event, index));
    },
    handleStockAlertModalState({
      isModalOpen = false,
      uncheckSizeInputs = false,
    }) {
      this.isStockAlertModalOpen = isModalOpen;
      if (!isModalOpen && !this.hasUniqueSize()) {
        this.selectedSize = null;
      }
      if (uncheckSizeInputs) this.uncheckSizeInputs();
    },
    handleAppExclusiveModalState({ isModalOpen = false }) {
      this.isAppExclusiveModalOpen = isModalOpen;
      if (!isModalOpen) {
        window.location.href = this.appsLandingUrl;
      }
    },
    handleModalClose() {
      this.handleStockAlertModalState({ isModalOpen: false });
      this.handleSizeSelectorMobileState({ isModalOpen: false });
      // Send info for analytics
      analyticsEventBus.publish(analyticsEventBus.EVENTS.stockAlertClosed);
    },
    handleProductAddedToCart() {
      if (this.isBreakpointMobile) {
        this.productAddedToCart = this.product;
        setTimeout(() => {
          this.productAddedToCart = null;
        }, 8000);
      }
    },
    openZoom(ordinal = 0, percentileY = 0) {
      if (this.isZoomOpen) return;
      this.zoomRequirements = {
        ordinal,
        percentileY,
      };
      this.isZoomOpen = true;
      this.$emit('zoomopen');
      this.$eventBus.$emit(
        EventBusConfig.events.TheProductDetails.zoomOpened,
        this.zoomRequirements
      );
      // trigger when any navigation event
      window.addEventListener('popstate', this.closeZoom, {
        once: true,
        passive: true,
      });
    },
    emitAnalyticsData() {
      if (!this.loading) {
        this.$eventBus.$emit(
          EventBusConfig.events.TheProductDetails.loaded,
          this.product
        );
      }
    },
    uncheckSizeInputs() {
      this.$refs.desktopSizeSelector?.uncheckInput();
      this.$refs.mobileSizeSelector?.uncheckInput();
    },
    openIframeModal() {
      this.showIframeModal = true;
      const virtualTryOnClicked = new CustomEvent('virtualTryOn:clicked');
      document.dispatchEvent(virtualTryOnClicked);
    },
  },
};
</script>
